import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { formatApiError } from "../lib/api";
import { supabase } from "../lib/supabase";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Download } from "lucide-react";
import { exportHistoryPdf } from "../lib/pdf";
import { localizeFoodName, localizeServing } from "../lib/foodTranslations";

const limits = { potassium: 3000, phosphorus: 1000, sodium: 2000, protein: 60 };
const emptyTotals = () => ({ potassium: 0, phosphorus: 0, sodium: 0, protein: 0 });
const logDate = (log) => (log.logged_at || new Date().toISOString()).slice(0, 10);
const readGuestFoodLogs = () => {
  if (typeof window === "undefined") return [];

  try {
    const savedLogs = localStorage.getItem("guestFoodLogs");
    const parsedLogs = savedLogs ? JSON.parse(savedLogs) : [];
    return Array.isArray(parsedLogs) ? parsedLogs : [];
  } catch {
    localStorage.removeItem("guestFoodLogs");
    return [];
  }
};
const logEntry = (log) => {
  const food = log.foods || {};
  const quantity = Number(log.quantity || 1);
  return {
    id: log.id,
    quantity,
    food_name: log.food_name || food.name || "Unknown food",
    category: log.category || food.category || "",
    serving_size: log.serving_size || food.serving_size || "",
    potassium: Math.round(Number(log.potassium_mg || 0)),
    phosphorus: Math.round(Number(log.phosphorus_mg || 0)),
    sodium: Math.round(Number(log.sodium_mg || 0)),
    protein: Math.round(Number(log.protein_g || 0)),
  };
};
const groupLogsByDay = (logs) => {
  const grouped = (logs || []).reduce((acc, log) => {
    const date = logDate(log);
    if (!acc[date]) acc[date] = { date, totals: emptyTotals(), limits, entries: [] };
    const entry = logEntry(log);
    acc[date].entries.push(entry);
    acc[date].totals.potassium += entry.potassium;
    acc[date].totals.phosphorus += entry.phosphorus;
    acc[date].totals.sodium += entry.sodium;
    acc[date].totals.protein += entry.protein;
    return acc;
  }, {});

  return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date)).slice(-28);
};

export default function History() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const guestMode =
    typeof window !== "undefined" &&
    localStorage.getItem("guestMode") === "true";
  const [days, setDays] = useState([]);
  const [selected, setSelected] = useState(null);
  const [profile, setProfile] = useState({
    full_name: "",
    weight_kg: 70,
  });
  const nutrientLabels = {
    potassium: t("potassium"),
    phosphorus: t("phosphorus"),
    sodium: t("sodium"),
    protein: t("protein"),
  };

  const loadProfile = useCallback(async () => {
    if (guestMode) {
      try {
        const savedProfile = localStorage.getItem("guestProfile");
        const guestProfile = savedProfile ? JSON.parse(savedProfile) : null;

        if (guestProfile) {
          setProfile({
            full_name: guestProfile.full_name || "",
            weight_kg: guestProfile.weight_kg || 70,
          });
        }
      } catch {
        localStorage.removeItem("guestProfile");
      }

      return;
    }

    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, weight_kg")
        .eq("id", user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setProfile({
          full_name: data.full_name || "",
          weight_kg: data.weight_kg || 70,
        });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [guestMode, user?.id]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    const load = async () => {
      try {
        if (guestMode) {
          const sorted = groupLogsByDay(readGuestFoodLogs());
          setDays(sorted);
          setSelected(sorted[sorted.length - 1] || null);
          return;
        }

        if (!user?.id) return;
        const { data, error } = await supabase
          .from("food_logs")
          .select("id, food_id, food_name, category, serving_size, quantity, potassium_mg, phosphorus_mg, sodium_mg, protein_g, logged_at, mixed_meal_ingredients, foods(name, category, serving_size)")
          .eq("user_id", user.id);
        if (error) throw error;
        const sorted = groupLogsByDay(data || []);
        setDays(sorted);
        setSelected(sorted[sorted.length - 1] || null);
      } catch (e) { toast.error(formatApiError(e)); }
    };
    load();
  }, [guestMode, user?.id]);

  const chartData = days.map((d) => ({
    date: d.date.slice(5),
    [nutrientLabels.potassium]: d.totals.potassium,
    [nutrientLabels.phosphorus]: d.totals.phosphorus,
    [nutrientLabels.sodium]: d.totals.sodium,
    [nutrientLabels.protein]: d.totals.protein,
  }));

  return (
    <div className="space-y-6" data-testid="history-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>{t("past_days")}</h1>
          <p className="text-muted-foreground mt-1">{t("days_28_trend")}</p>
        </div>
        <Button variant="outline" onClick={() => exportHistoryPdf(days, profile.full_name || user?.name || "-", profile.weight_kg)} disabled={!days.length} className="gap-2" data-testid="history-export-pdf">
          <Download className="w-4 h-4" /> {t("export_pdf")}
        </Button>
      </div>

      <div className="bento-card">
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--rs-border)" />
            <XAxis dataKey="date" stroke="var(--rs-muted)" />
            <YAxis stroke="var(--rs-muted)" />
            <Tooltip contentStyle={{ background: "var(--rs-surface)", border: "1px solid var(--rs-border)", borderRadius: 12 }} />
            <Legend />
            <Line type="monotone" dataKey={nutrientLabels.potassium} stroke="#E07A5F" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey={nutrientLabels.phosphorus} stroke="#3D5A80" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey={nutrientLabels.sodium} stroke="#81B29A" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey={nutrientLabels.protein} stroke="#F2CC8F" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-2">
          {days.slice().reverse().map((d) => (
            <button
              key={d.date}
              onClick={() => setSelected(d)}
              className={`w-full text-start bento-card transition-all ${selected?.date === d.date ? "ring-2 ring-primary" : ""}`}
              data-testid={`history-day-${d.date}`}
            >
              <div className="font-semibold">{d.date}</div>
              <div className="text-xs text-muted-foreground mt-1">{d.entries.length} {t("entries")}</div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-2 bento-card">
          {selected ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-xl">{selected.date}</h3>
                <span className="text-sm text-muted-foreground">{selected.entries.length} {t("entries")}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {["potassium", "phosphorus", "sodium", "protein"].map((k) => {
                  const limit = selected.limits[k];
                  const val = selected.totals[k];
                  const ok = val <= limit;
                  return (
                    <div key={k} className="p-3 rounded-xl border border-border">
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">{t(k)}</div>
                      <div className="text-xl font-extrabold mt-1" style={{ color: `var(--rs-${k})`, fontFamily: "IBM Plex Sans" }}>{val}<span className="text-xs text-muted-foreground"> / {limit}</span></div>
                      <div className={`text-[10px] mt-1 font-semibold ${ok ? "text-emerald-600" : "text-destructive"}`}>{ok ? t("safe") : t("exceeded")}</div>
                    </div>
                  );
                })}
              </div>
              {selected.entries.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">{t("no_entries")}</div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {selected.entries.map((e) => (
                    <div key={e.id} className="p-3 rounded-lg border border-border">
                      <div className="font-semibold text-sm">{localizeFoodName(e.food_name, lang)} × {e.quantity}</div>
                      {e.serving_size && (
                        <div className="text-xs text-muted-foreground">{localizeServing(e.serving_size, lang)}</div>
                      )}
                      <div className="text-xs text-muted-foreground">K {e.potassium} · P {e.phosphorus} · Na {e.sodium} · Pr {e.protein}g</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-muted-foreground">{t("select_day")}</div>
          )}
        </div>
      </div>
    </div>
  );
}
